/**
 * Cabinet (comptable / fiscaliste) settings sections — config-driven.
 *
 * Each SectionDef maps a settings-service category to a UI page that loads
 *   GET /api/v1/settings/tenant/{tenantId}/{category}
 * and saves
 *   PUT /api/v1/settings/tenant/{tenantId}/{category}  body {settings:{...}}
 *
 * The generic SettingsSectionPage renders the groups[] of FieldDefs.
 */

import type { LucideIcon } from 'lucide-react'
import {
  Calculator,
  Receipt,
  ClipboardCheck,
  Banknote,
  Sparkles,
} from 'lucide-react'

export type FieldType =
  | 'text'
  | 'number'
  | 'select'
  | 'switch'
  | 'date'
  | 'textarea'
  | 'tags'

export interface FieldDef {
  key: string
  label: string
  type: FieldType
  /** for select / tags suggestions */
  options?: { value: string; label: string }[]
  placeholder?: string
  help?: string
  /** sensitive -> rendered masked (type=password) */
  sensitive?: boolean
}

export interface GroupDef {
  title: string
  description?: string
  fields: FieldDef[]
}

export interface SectionDef {
  /** URL slug under /settings/<slug> */
  slug: string
  /** settings-service category segment */
  category: string
  title: string
  description: string
  icon: LucideIcon
  groups: GroupDef[]
}

export const CABINET_SETTINGS_SECTIONS: SectionDef[] = [
  // 1. Accounting -------------------------------------------------------
  {
    slug: 'accounting',
    category: 'accounting',
    title: 'Comptabilite',
    description: 'Normes comptables, plans de comptes et automatisation',
    icon: Calculator,
    groups: [
      {
        title: 'Normes & referentiels',
        description: 'Referentiel comptable applique par le cabinet',
        fields: [
          {
            key: 'standards',
            label: 'Norme comptable',
            type: 'select',
            help: 'Referentiel applique par defaut aux dossiers clients',
            options: [
              { value: 'NCECF', label: 'NCECF — Normes pour entreprises a capital ferme' },
              { value: 'IFRS', label: 'IFRS — Normes internationales' },
              { value: 'ASPE', label: 'ASPE — Accounting Standards for Private Enterprises' },
            ],
          },
        ],
      },
      {
        title: 'Plans comptables',
        fields: [
          {
            key: 'coa_templates',
            label: 'Plans comptables types (par secteur)',
            type: 'tags',
            placeholder: 'Ex: Commerce de detail, Construction, OBNL...',
            help: 'Ajoutez les plans comptables types proposes selon le secteur du client',
          },
        ],
      },
      {
        title: 'Automatisation',
        fields: [
          {
            key: 'automation_rules',
            label: 'Regles auto-categorisation OCR',
            type: 'textarea',
            placeholder:
              'Ex: "facture Hydro-Quebec" -> 6210 Electricite\n"depot Interac" -> 1010 Encaisse',
            help: 'Une regle par ligne, appliquee au moment de la lecture OCR des pieces',
          },
        ],
      },
    ],
  },

  // 2. Tax-prep ---------------------------------------------------------
  {
    slug: 'tax',
    category: 'tax',
    title: 'Preparation fiscale',
    description: 'Acces ARC/RQ, transmission electronique et listes de controle',
    icon: Receipt,
    groups: [
      {
        title: 'Acces transmission (sensible)',
        description: 'Identifiants de transmission electronique — stockes de maniere securisee',
        fields: [
          {
            key: 'cra_rq_credentials_efile',
            label: 'Numero EFILE (ARC / RQ)',
            type: 'text',
            sensitive: true,
            placeholder: 'Ex: A1234',
            help: 'Numero EFILE utilise pour la transmission electronique des declarations',
          },
          {
            key: 'netfile_access',
            label: 'Acces NETFILE',
            type: 'text',
            sensitive: true,
            placeholder: 'Code d acces NETFILE',
          },
        ],
      },
      {
        title: 'Listes de controle',
        fields: [
          {
            key: 'checklist_libraries',
            label: 'Bibliotheques de listes de controle',
            type: 'tags',
            help: 'Types de declarations couverts par les listes de controle du cabinet',
            options: [
              { value: 'T1', label: 'T1 — Particuliers' },
              { value: 'T2', label: 'T2 — Societes' },
              { value: 'CO-17', label: 'CO-17 — Societes (Quebec)' },
              { value: 'TPS-TVQ', label: 'TPS-TVQ' },
            ],
          },
        ],
      },
    ],
  },

  // 3. Audit-Finance ----------------------------------------------------
  {
    slug: 'audit-finance',
    category: 'audit_finance',
    title: 'Audit & Finance',
    description: 'Methodologie de mission et modeles de feuilles de travail',
    icon: ClipboardCheck,
    groups: [
      {
        title: 'Methodologie',
        fields: [
          {
            key: 'methodology',
            label: 'Type de mission',
            type: 'select',
            help: 'Norme de certification appliquee par defaut',
            options: [
              { value: 'NCMC', label: 'NCMC — Mission de compilation' },
              { value: 'NCMR', label: 'NCMR — Mission d examen' },
              { value: 'NAGR', label: 'NAGR — Audit (normes generalement reconnues)' },
            ],
          },
        ],
      },
      {
        title: 'Feuilles de travail',
        fields: [
          {
            key: 'working_paper_templates',
            label: 'Modeles de feuilles de travail',
            type: 'tags',
            placeholder: 'Ex: Lead sheet bilan, Continuite exploitation...',
            help: 'Modeles type CaseWare proposes pour les dossiers de mission',
          },
        ],
      },
    ],
  },

  // 4. Payroll ----------------------------------------------------------
  {
    slug: 'payroll',
    category: 'payroll',
    title: 'Paie',
    description: 'Formulaires de fin d annee et automatisation',
    icon: Banknote,
    groups: [
      {
        title: 'Fin d annee',
        fields: [
          {
            key: 'year_end_forms',
            label: 'Formulaires de fin d annee',
            type: 'tags',
            help: 'Feuillets generes pour la production de fin d annee',
            options: [
              { value: 'T4', label: 'T4 — Etat de la remuneration (federal)' },
              { value: 'R1', label: 'RL-1 — Revenus d emploi (Quebec)' },
              { value: 'Sommaire', label: 'Sommaire' },
            ],
          },
          {
            key: 'automate_year_end',
            label: 'Automatiser la production de fin d annee',
            type: 'switch',
            help: 'Genere automatiquement les feuillets a la cloture de l annee',
          },
        ],
      },
    ],
  },

  // 5. LLM --------------------------------------------------------------
  {
    slug: 'llm',
    category: 'llm',
    title: 'Assistant IA',
    description: 'Prompts comptables par type de tache',
    icon: Sparkles,
    groups: [
      {
        title: 'Prompts comptables',
        description: 'Instructions transmises a l assistant IA selon le type de tache',
        fields: [
          {
            key: 'accounting_prompts_compilation',
            label: 'Prompt — Compilation (NCMC)',
            type: 'textarea',
            placeholder: 'Instructions pour les missions de compilation...',
          },
          {
            key: 'accounting_prompts_examen',
            label: 'Prompt — Examen (NCMR)',
            type: 'textarea',
            placeholder: 'Instructions pour les missions d examen...',
          },
          {
            key: 'accounting_prompts_t1',
            label: 'Prompt — T1 (particuliers)',
            type: 'textarea',
            placeholder: 'Instructions pour la preparation des T1...',
          },
          {
            key: 'accounting_prompts_t2',
            label: 'Prompt — T2 (societes)',
            type: 'textarea',
            placeholder: 'Instructions pour la preparation des T2...',
          },
        ],
      },
    ],
  },
]

export function getSectionBySlug(slug: string): SectionDef | undefined {
  return CABINET_SETTINGS_SECTIONS.find((s) => s.slug === slug)
}
